/**
 * Utility functions for handling flyerOrientation localStorage with design/campaign ID support
 */

import { DocumentOrientation } from "../drawer/types";

/**
 * Gets the orientation storage key based on available IDs
 * @param designId - Design ID from URL params or context
 * @param campaignId - Campaign ID from URL params or context
 * @returns Storage key for flyerOrientation
 */
export const getOrientationStorageKey = (designId?: string | null, campaignId?: string | null): string => {
  const id = designId || campaignId;
  return id ? `flyerOrientation_${id}` : "flyerOrientation";
};

/**
 * Gets orientation from localStorage using the appropriate key
 * @param designId - Design ID from URL params or context
 * @param campaignId - Campaign ID from URL params or context
 * @param defaultOrientation - Default orientation to return if none found
 * @returns Stored orientation or default
 */
export const getStoredOrientation = (
  designId?: string | null,
  campaignId?: string | null,
  defaultOrientation: DocumentOrientation = DocumentOrientation.Portrait
): string => {
  const key = getOrientationStorageKey(designId, campaignId);
  return localStorage.getItem(key) || defaultOrientation;
};

/**
 * Sets orientation in localStorage using the appropriate key
 * @param orientation - Orientation to store
 * @param designId - Design ID from URL params or context
 * @param campaignId - Campaign ID from URL params or context
 */
export const setStoredOrientation = (
  orientation: string,
  designId?: string | null,
  campaignId?: string | null
): void => {
  const key = getOrientationStorageKey(designId, campaignId);
  localStorage.setItem(key, orientation);
};

/**
 * Extracts design and campaign IDs from the current URL
 * This is useful for utility functions that don't have access to React context
 * @returns Object with designId and campaignId from URL
 */
export const extractIDsFromURL = (): { designId?: string; campaignId?: string } => {
  const pathname = window.location.pathname;

  // Public builder pattern: /public/flyer-builder/:countryCode/:roomId/:format
  const publicMatch = pathname.match(/\/public\/flyer-builder\/[^/]+\/([^/]+)/);
  if (publicMatch) {
    return { designId: publicMatch[1] };
  }

  // Regular builder pattern: /campaign/:campaignId/client/:clientId/flyer-builder/:format
  const regularMatch = pathname.match(/\/campaign\/([^/]+)\/client\/([^/]+)\/flyer-builder/);
  if (regularMatch) {
    return { campaignId: regularMatch[1] };
  }

  return {};
};
